import { useEffect, useState } from "react";
import {
  Typography,
  Button,
  Card,
  CardContent,
  Switch,
  Box,
  Chip,
  Fade,
  CircularProgress,
  Backdrop,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar, // 👈 ADDED
  Alert     // 👈 ADDED
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { getMyWork, toggleStatus } from "../api/deliveryApi";
import { useNavigate } from "react-router-dom";
import deliveryboy from "../assets/deliveryboy.png";

// =========================
// DESIGN TOKENS
// =========================
const tokens = {
  ink: "#1E2230",
  muted: "#6B7280",
  paperTop: "#EFF2F8",
  paperBottom: "#DFE4EE",
  surface: "#FFFFFF",
  surfaceEdge: "#D7DCE6",
  brand: "#FF8A3D", 
  brandEdge: "#D9631A",
  brandDark: "#26282F",
  go: "#2FCB6B",
  goEdge: "#1C9A4B",
  stop: "#FF5A6E",
  stopEdge: "#D62F45",
  amber: "#FFC542",
  amberEdge: "#E39C0C",
  info: "#4C8DFF",
  infoEdge: "#2E63D6",
};

const statusStyle = {
  IDLE: { bg: tokens.go, edge: tokens.goEdge },
  BUSY: { bg: tokens.amber, edge: tokens.amberEdge },
  ON_DELIVERY: { bg: tokens.info, edge: tokens.infoEdge },
  OFFLINE: { bg: "#9CA3AF", edge: "#6B7280" },
};

const useBrandFonts = () => {
  useEffect(() => {
    if (document.getElementById("mealno-font-link")) return;
    const link = document.createElement("link");
    link.id = "mealno-font-link";
    link.rel = "stylesheet";
    link.href =
      "https://fonts.googleapis.com/css2?family=Baloo+2:wght@600;700;800&family=Inter:wght@400;500;600;700&family=JetBrains+Mono:wght@500;600&display=swap";
    document.head.appendChild(link);
  }, []);
};

const raised = (bg, edge, depth = 6) => ({
  background: bg,
  boxShadow: `0 ${depth}px 0 ${edge}, 0 ${depth + 10}px 22px -6px rgba(30,34,48,0.25)`,
});

const ChunkySwitch = styled(Switch)(() => ({
  width: 76,
  height: 42,
  padding: 0,
  "& .MuiSwitch-switchBase": {
    padding: 5,
    "&.Mui-checked": {
      transform: "translateX(34px)",
      "& + .MuiSwitch-track": { backgroundColor: "#DCE3EE", opacity: 1 },
      "& .MuiSwitch-thumb": {
        backgroundColor: tokens.go,
        boxShadow: `0 3px 0 ${tokens.goEdge}, 0 3px 8px rgba(0,0,0,0.25)`,
      },
    },
  },
  "& .MuiSwitch-thumb": {
    width: 32,
    height: 32,
    backgroundColor: tokens.stop,
    boxShadow: `0 3px 0 ${tokens.stopEdge}, 0 3px 8px rgba(0,0,0,0.25)`,
    transition: "box-shadow 180ms ease, background-color 180ms ease, transform 180ms ease",
  },
  "& .MuiSwitch-track": {
    borderRadius: 21,
    backgroundColor: "#DCE3EE",
    opacity: 1,
    boxShadow: "inset 0 3px 6px rgba(30,34,48,0.18)",
  },
}));

const cardStyle = {
  ...raised(tokens.surface, tokens.surfaceEdge, 8),
  borderRadius: "26px",
  position: "relative",
};

const IconBadge = ({ children, bg, edge, size = 64 }) => (
  <Box
    sx={{
      width: size,
      height: size,
      borderRadius: "50%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: size * 0.42,
      ...raised(bg, edge, 5),
      flexShrink: 0,
    }}
  >
    {children}
  </Box>
);

const button3DStyle = (bg, edge) => ({
  borderRadius: "16px",
  py: 1.6,
  fontWeight: 800,
  textTransform: "none",
  fontSize: "1.02rem",
  fontFamily: "'Baloo 2', sans-serif",
  letterSpacing: "0.2px",
  background: bg,
  color: "#fff",
  boxShadow: `0 6px 0 ${edge}`,
  transition: "transform 100ms ease, box-shadow 100ms ease",
  "&:hover": { background: bg, opacity: 0.96 },
  "&:active": {
    transform: "translateY(6px)",
    boxShadow: `0 0 0 ${edge}`,
  },
});

export default function Dashboard() {
  useBrandFonts();

  const [data, setData] = useState(null);
  const [online, setOnline] = useState(false);
  const [loading, setLoading] = useState(true);
  const [openOfflineAlert, setOpenOfflineAlert] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [loadingText, setLoadingText] = useState("");
  const navigate = useNavigate();

  // 👈 ADDED SNACKBAR STATE
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "error" });

  const fetchWork = async (showLoader = false) => {
    try {
      if (showLoader) {
        setLoading(true);
        setActionLoading(true);
        setLoadingText("Loading dashboard...");
      }
      const res = await getMyWork();
      setData(res.data);
      setOnline(res.data?.delivery_status !== "OFFLINE");
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
      setActionLoading(false);
    }
  };

  useEffect(() => {
    fetchWork(true);
    const interval = setInterval(() => {
      fetchWork(false);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const handleToggle = async () => {
    if (online && (data?.type === "ORDER" || data?.type === "BATCH")) {
      setOpenOfflineAlert(true);
      return;
    }
    try {
      setActionLoading(true);
      setLoadingText(online ? "Going offline..." : "Going online...");
      const res = await toggleStatus();
      setOnline(res.data.active);
      await fetchWork(false);
      setActionLoading(false);
    } catch (err) {
      setActionLoading(false);
      // 👈 REPLACED ALERT WITH SNACKBAR
      setSnackbar({
        open: true,
        message: err.response?.data?.error || "Error toggling status",
        severity: "error"
      });
    }
  };

  const hour = new Date().getHours();
  const timeGreeting =
    hour < 12 ? "Good Morning" : hour < 18 ? "Good Afternoon" : "Good Evening";

  const activeStatus = statusStyle[data?.delivery_status] || statusStyle.OFFLINE;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: `linear-gradient(180deg, ${tokens.paperTop} 0%, ${tokens.paperBottom} 100%)`,
        fontFamily: "'Inter', sans-serif",
        pt: { xs: 10, sm: 12 }, // 👈 ADDED to push content below the top navbar
        pb: { xs: 10, sm: 12 }, // 👈 ADDED to keep content above the bottom navbar
      }}
    >
      <Box sx={{ maxWidth: "520px", mx: "auto", px: { xs: 2.5, md: 4 } }}>
        <Box mb={3} mt={2} sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <IconBadge bg="#FFE3CC" edge="#F3B784" size={68}>
            <Box
              component="img"
              src={deliveryboy}
              alt="Delivery Boy"
              sx={{
                width: 42,
                height: 42,
                objectFit: "contain",
                transform: "scaleX(-1)",
                filter:
                  "invert(54%) sepia(93%) saturate(1432%) hue-rotate(349deg) brightness(101%) contrast(101%)",
              }}
            />
          </IconBadge>
          <Box>
            <Typography
              sx={{
                fontFamily: "'Baloo 2', sans-serif",
                fontWeight: 700,
                color: tokens.ink,
                letterSpacing: "-0.2px",
                fontSize: { xs: "1.4rem", sm: "1.6rem" },
                lineHeight: 1.15,
              }}
            >
              {timeGreeting}, {data?.delivery_boy_name?.split(" ")[0] || "Partner"} 👋
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  backgroundColor: online ? tokens.go : tokens.stop,
                  boxShadow: `0 0 0 3px ${online ? "rgba(47,203,107,0.25)" : "rgba(255,90,110,0.25)"}`,
                }}
              />
              <Typography sx={{ color: tokens.muted, fontSize: "0.88rem" }}>
                Ready for your next MealNo delivery?
              </Typography>
            </Box>
          </Box>
        </Box>

        <Card sx={{ ...cardStyle, p: 3, mb: 3 }} elevation={0}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Box>
              <Typography
                sx={{
                  color: tokens.muted,
                  fontWeight: 700,
                  fontSize: "0.72rem",
                  letterSpacing: "1.2px",
                  textTransform: "uppercase",
                  mb: 0.5,
                }}
              >
                Current Status
              </Typography>
              <Typography
                sx={{
                  fontFamily: "'Baloo 2', sans-serif",
                  fontWeight: 700,
                  fontSize: "1.5rem",
                  color: online ? tokens.goEdge : tokens.stopEdge,
                }}
              >
                {online ? "Online" : "Offline"}
              </Typography>
            </Box>
            <ChunkySwitch checked={online} onChange={handleToggle} />
          </Box>

          <Box
            sx={{
              mt: 2.5,
              pt: 2.5,
              borderTop: `2px dashed ${tokens.paperBottom}`,
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              flexWrap: "wrap",
            }}
          >
            <Typography sx={{ fontSize: "0.85rem", fontWeight: 600, color: tokens.muted }}>
              Activity State
            </Typography>
            <Chip
              label={data?.delivery_status || "OFFLINE"}
              variant="filled"
              sx={{
                fontFamily: "'JetBrains Mono', monospace",
                fontWeight: 700,
                fontSize: "0.72rem",
                letterSpacing: "0.4px",
                borderRadius: "10px",
                height: 30,
                px: 0.5,
                color: "#fff",
                background: activeStatus.bg,
                boxShadow: `0 3px 0 ${activeStatus.edge}`,
              }}
            />
          </Box>
        </Card>

        <Fade in={!loading && data?.type === "NONE"}>
          <Box sx={{ display: data?.type === "NONE" ? "block" : "none" }}>
            <Card sx={{ ...cardStyle, textAlign: "center", py: 5, px: 2 }} elevation={0}>
              <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
                <IconBadge bg="#FFE1E6" edge="#F3AEB9" size={78}>
                  🛵
                </IconBadge>
              </Box>
              <Typography
                sx={{
                  fontFamily: "'Baloo 2', sans-serif",
                  fontWeight: 700,
                  color: tokens.ink,
                  fontSize: "1.15rem",
                }}
              >
                No Active Deliveries
              </Typography>
              <Typography sx={{ color: tokens.muted, mt: 1, fontSize: "0.9rem" }}>
                Stay online to receive new orders and start delivering.
              </Typography>
            </Card>
          </Box>
        </Fade>

        <Fade in={!loading && data?.type === "BATCH"}>
          <Box sx={{ display: data?.type === "BATCH" ? "block" : "none" }}>
            <Card sx={{ ...cardStyle, p: 0, overflow: "hidden" }} elevation={0}>
              <Box
                sx={{
                  background: tokens.brand,
                  px: 3,
                  py: 2.25,
                  color: "#fff",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography
                  sx={{
                    fontFamily: "'Baloo 2', sans-serif",
                    fontWeight: 700,
                    fontSize: "1.1rem",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  📦 New Batch
                </Typography>
                <Chip
                  label={data?.status}
                  size="small"
                  sx={{
                    fontFamily: "'JetBrains Mono', monospace",
                    fontWeight: 700,
                    fontSize: "0.7rem",
                    backgroundColor: "rgba(255,255,255,0.22)",
                    color: "#fff",
                  }}
                />
              </Box>
              <CardContent sx={{ p: 3 }}>
                <Typography
                  sx={{
                    fontFamily: "'JetBrains Mono', monospace",
                    color: tokens.muted,
                    mb: 3,
                    fontSize: "0.9rem",
                    fontWeight: 700,
                  }}
                >
                  BATCH #{data?.batch_id}
                </Typography>
                <Button
                  fullWidth
                  variant="contained"
                  disableElevation
                  onClick={() => navigate(`/batch/${data?.batch_id}`)}
                  sx={button3DStyle(tokens.brandDark, "#0F1116")}
                >
                  View Batch Details
                </Button>
              </CardContent>
            </Card>
          </Box>
        </Fade>

        <Fade in={!loading && data?.type === "ORDER"}>
          <Box sx={{ display: data?.type === "ORDER" ? "block" : "none" }}>
            <Card sx={{ ...cardStyle, p: 0, overflow: "hidden" }} elevation={0}>
              <Box
                sx={{
                  background: tokens.info,
                  px: 3,
                  py: 2.25,
                  color: "#fff",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography
                  sx={{
                    fontFamily: "'Baloo 2', sans-serif",
                    fontWeight: 700,
                    fontSize: "1.1rem",
                  }}
                >
                  🛍️ New Order
                </Typography>
                <Chip
                  label={data?.status}
                  size="small"
                  sx={{
                    fontFamily: "'JetBrains Mono', monospace",
                    fontWeight: 700,
                    fontSize: "0.7rem",
                    backgroundColor: "rgba(255,255,255,0.22)",
                    color: "#fff",
                  }}
                />
              </Box>
              <CardContent sx={{ p: 3 }}>
                <Typography
                  sx={{
                    fontFamily: "'JetBrains Mono', monospace",
                    color: tokens.muted,
                    mb: 3,
                    fontSize: "0.9rem",
                    fontWeight: 700,
                  }}
                >
                  ORDER #{data?.order_id}
                </Typography>
                <Button
                  fullWidth
                  variant="contained"
                  disableElevation
                  onClick={() => navigate(`/order/${data?.order_id}`)}
                  sx={button3DStyle(tokens.brandDark, "#0F1116")}
                >
                  View Order Details
                </Button>
              </CardContent>
            </Card>
          </Box>
        </Fade>

        <Dialog
          open={openOfflineAlert}
          onClose={() => setOpenOfflineAlert(false)}
          PaperProps={{
            sx: {
              borderRadius: "24px",
              p: 1,
              background: "#fff",
              maxWidth: "360px",
              width: "100%",
              boxShadow: `0 10px 0 ${tokens.stopEdge}, 0 30px 50px rgba(30,34,48,0.3)`,
            },
          }}
        >
          <DialogTitle
            sx={{
              textAlign: "center",
              fontFamily: "'Baloo 2', sans-serif",
              fontWeight: 700,
              color: tokens.stopEdge,
              fontSize: "1.25rem",
            }}
          >
            🚫 Cannot Go Offline
          </DialogTitle>
          <DialogContent>
            <Typography sx={{ textAlign: "center", color: tokens.muted, lineHeight: 1.7, mt: 1, fontSize: "0.92rem" }}>
              You already have an active delivery assigned.
              <br />
              Please complete your current order before going offline.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ justifyContent: "center", pb: 2.5 }}>
            <Button
              variant="contained"
              disableElevation
              onClick={() => setOpenOfflineAlert(false)}
              sx={{ ...button3DStyle(tokens.stop, tokens.stopEdge), px: 4, width: "auto" }}
            >
              Got it
            </Button>
          </DialogActions>
        </Dialog>
      </Box>

      <Backdrop
        open={actionLoading}
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 9999,
          backdropFilter: "blur(6px)",
          background: "rgba(20,22,28,0.6)",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <Box
          sx={{
            ...raised(tokens.surface, tokens.surfaceEdge, 5),
            borderRadius: "50%",
            p: 1.5,
            display: "flex",
          }}
        >
          <CircularProgress size={46} thickness={4} sx={{ color: tokens.brand }} />
        </Box>
        <Typography
          sx={{
            fontFamily: "'JetBrains Mono', monospace",
            fontWeight: 600,
            fontSize: "0.9rem",
            letterSpacing: "0.5px",
          }}
        >
          {loadingText || "Please wait..."}
        </Typography>
      </Backdrop>

      {/* 👈 ADDED TOP SNACKBAR */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        sx={{ top: { xs: 20, sm: 24 } }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%", borderRadius: "12px", fontWeight: "bold", boxShadow: "0 4px 12px rgba(0,0,0,0.15)" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}